// src/components/DataTable.tsx
import * as React from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface Column<T> {
  key: string
  header: string
  /** Korrektur #14: optionale reiche Kopfzelle (z. B. Master-Checkbox) — hat Vorrang
   *  vor dem reinen `header`-Text, der als Fallback bestehen bleibt. */
  headerCell?: () => React.ReactNode
  cell?: (row: T) => React.ReactNode
  className?: string
  /** Modul 38 A7: Spaltenbreite in px, gesetzt an der Kopfzelle (im festen Layout
   *  bestimmt die erste Zeile die Spaltenbreite).
   *
   *  Sie wird IMMER gesetzt, auch ohne `tableFixed` — dort bleibt sie aber ein blosser
   *  Wunsch, den der Browser zugunsten des Inhalts ueberstimmen darf. Verbindlich wird
   *  sie erst mit `tableFixed`. Genau daran scheiterte die Reichweiten-Tabelle: eine
   *  lange Kopfzeile zog ihre Spalte auf, und die Tabelle lief aus dem Bild.
   *  (Der Kommentar sagte zuerst „wirkt nur zusammen mit `tableFixed`" — das war eine
   *  Zusage, die der Code nicht einhielt; Codex-Review 2026-08-18, Befund 7.) */
  width?: number
  /** Modul 38 A7: vollstaendiger Name der Spalte fuer Screenreader, wenn die sichtbare
   *  Kopfzeile gekuerzt oder leer ist. Landet als `aria-label` am `<th>` und ersetzt
   *  dort den sichtbaren Text als zugaenglichen Namen.
   *
   *  Ein `title` an einem `<span>` reicht dafuer NICHT: Screenreader lesen bei
   *  vorhandenem sichtbarem Text weiterhin „Bestellt" statt „Offene Bestellungen", und
   *  eine Spalte ohne Kopftext bliebe ganz namenlos (Gemini-Befund 4 / Codex-Befund 5). */
  headerAriaLabel?: string
}

export interface DataTablePagination {
  hasMore: boolean
  onNext: () => void
  onPrev: () => void
  canPrev: boolean
  count: number
}

export interface DataTableProps<T extends object> {
  data: T[]
  columns: Column<T>[]
  onRowClick?: (row: T) => void
  pagination?: DataTablePagination
  isLoading?: boolean
  emptyMessage?: string
  rowKey?: (row: T) => string | number
  /** Korrektur #17: Kopfzeile bleibt beim Scrollen sichtbar, mit opakem Hintergrund
   *  (bg-panel), damit durchscrollende Zeilen nicht durchscheinen.
   *
   *  Plan 2026-10-26 (Nutzerbefund Reichweite): Es scrollt nur noch die SEITE. Der fruehere
   *  eigene Scrollport der Tabelle (`max-h-[70vh]`) begann mitten auf der Seite und ragte
   *  unten aus dem Fenster — das Mausrad blieb in der Tabelle haengen, Zeilen und
   *  Blaetterung lagen ausserhalb des Bildes. Damit der Kopf an der Seite klebt, darf kein
   *  Vorfahr ein Scrollport sein:
   *   - `data-seiten-scroll` schaltet in Layout.tsx `<main>` auf `overflow-x: clip`
   *     (`overflow-auto` machte main zum Scrollport, der nie scrollt — der Kopf klebte nie);
   *   - der shadcn-Wrapper (`overflow-x-auto`, computed auch `overflow-y: auto`) wird ab
   *     1600 px ebenfalls `clip`. Dort passt die Tabelle nachweislich (reach-spalten.ts).
   *     Darunter behaelt er seinen waagrechten Rollbalken; der Kopf klebt dann nicht, aber
   *     es gibt weiter nur einen senkrechten Rollbalken.
   *  `top-14` = Hoehe der klebenden StahliaTopBar (`h-14`). */
  stickyHeader?: boolean
  /** Modul 38 A7: `table-layout: fixed`. Die Spaltenbreiten aus `Column.width` gelten
   *  dann verbindlich, ueberlange Inhalte kuerzen statt die Tabelle zu dehnen. Spalten
   *  ohne `width` teilen sich den Rest. */
  tableFixed?: boolean
}

export function DataTable<T extends object>({
  data,
  columns,
  onRowClick,
  pagination,
  isLoading = false,
  emptyMessage,
  rowKey,
  stickyHeader = false,
  tableFixed = false,
}: DataTableProps<T>) {
  return (
    <div
      data-seiten-scroll={stickyHeader ? "" : undefined}
      className={cn(
        "w-full",
        stickyHeader && "min-[1600px]:[&_[data-slot=table-container]]:overflow-x-clip",
      )}
    >
      <Table className={cn(tableFixed && "table-fixed")}>
        <TableHeader className={cn(stickyHeader && "sticky top-14 z-10 bg-panel shadow-[0_1px_0_var(--line)]")}>
          <TableRow>
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={col.className}
                style={col.width === undefined ? undefined : { width: `${col.width}px` }}
                aria-label={col.headerAriaLabel}
              >
                {col.headerCell ? col.headerCell() : col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center py-8 text-muted-foreground"
              >
                Lade...
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center py-8 text-muted-foreground"
              >
                {emptyMessage ?? "Keine Einträge."}
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, rowIndex) => (
              <TableRow
                key={rowKey ? rowKey(row) : rowIndex}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  onRowClick && "cursor-pointer hover:bg-muted/50"
                )}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} className={col.className}>
                    {col.cell
                      ? col.cell(row)
                      : String(row[col.key as keyof T] ?? "—")}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {pagination && (
        <div className="flex items-center justify-between px-2 py-3 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={pagination.onPrev}
            disabled={!pagination.canPrev}
          >
            ← Zurück
          </Button>
          <span className="text-sm text-muted-foreground">
            {pagination.count} Einträge
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={pagination.onNext}
            disabled={!pagination.hasMore}
          >
            Weiter →
          </Button>
        </div>
      )}
    </div>
  )
}
